import loglevel from 'loglevel';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';

class LoglevelSentry {
  constructor(sentry) {
    _defineProperty(this, "sentry", void 0);
    _defineProperty(this, "category", void 0);
    this.sentry = sentry;
    this.category = "loglevel-sentry";
  }
  static translateError(args) {
    // Find first Error or create an "unknown" Error to keep stack trace.
    const index = args.findIndex(arg => arg instanceof Error);
    const msgIndex = args.findIndex(arg => typeof arg === "string");
    const apiErrorIndex = args.findIndex(arg => arg && typeof arg === "object" && "status" in arg && "type" in arg);
    let err;
    if (apiErrorIndex !== -1) {
      const apiError = args.splice(apiErrorIndex, 1)[0];
      err = new Error(`${apiError.status} ${apiError.type.toString()} ${apiError.statusText}`);
      args.push(apiError.url);
    } else if (index !== -1) {
      err = args.splice(index, 1)[0];
    } else if (msgIndex !== -1) {
      err = new Error(args.splice(msgIndex, 1)[0]);
    } else {
      err = new Error("Unknown error");
    }
    return [err, args];
  }
  static translateArgs(args) {
    const msgIndex = args.findIndex(arg => typeof arg === "string");
    const firstMsg = msgIndex !== -1 ? args.splice(msgIndex, 1)[0] : undefined;
    return firstMsg ? {
      message: firstMsg,
      // args is already spliced
      data: {
        arguments: args
      }
    } : {
      data: {
        arguments: args
      }
    };
  }
  static translateLevel(level) {
    switch (level) {
      case "info":
        return "info";
      case "warn":
        return "warning";
      default:
        return "debug";
    }
  }
  install(logger) {
    var _this = this;
    const defaultMethodFactory = logger.methodFactory;
    logger.methodFactory = (method, level, name) => {
      if (name) this.category = name.toString();
      const defaultMethod = defaultMethodFactory(method, level, name);
      switch (method) {
        case "error":
          return function () {
            for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
            }
            const [err, otherArgs] = LoglevelSentry.translateError(args);
            _this.error(err, ...otherArgs);
            if (defaultMethod) defaultMethod(err, ...otherArgs);
          };
        default:
          return function () {
            for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
              args[_key2] = arguments[_key2];
            }
            _this.log(LoglevelSentry.translateLevel(method), ...args);
            if (defaultMethod) defaultMethod(...args);
          };
      }
    };
    logger.setLevel(logger.getLevel());
  }
  setEnabled(enabled) {
    this.sentry.getCurrentHub().getClient().getOptions().enabled = enabled;
  }
  isEnabled() {
    return this.sentry.getCurrentHub().getClient().getOptions().enabled;
  }
  log(level) {
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }
    this.sentry.addBreadcrumb(_objectSpread(_objectSpread({}, LoglevelSentry.translateArgs(args)), {}, {
      category: this.category,
      level,
      timestamp: Date.now()
    }));
  }
  trace() {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    this.log("debug", ...args);
  }
  error(err) {
    for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
      args[_key5 - 1] = arguments[_key5];
    }
    this.sentry.captureException(err, {
      tags: {
        logger: "loglevel-sentry",
        "logger.name": this.category
      },
      extra: {
        arguments: args
      }
    });
  }
}

function createLogger(name, sentry) {
  const logger = loglevel.getLogger(name);
  logger.enableAll();
  const plugin = new LoglevelSentry(sentry);
  plugin.install(logger);
  return logger;
}

const defaultPattern = /(private|key|secret|authorization|address|email)+/i;
function isHex(s) {
  return /^(-0x|0x)?[0-9a-f]*$/i.test(s);
}
function redactEventData(data) {
  let keyPattern = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultPattern;
  if (typeof data !== "object" || data === null) return data;
  const keys = Object.keys(data);
  for (const k of keys) {
    const v = data[k];
    if (typeof v === "string" && keyPattern.test(k)) data[k] = "***";else data[k] = redactEventData(v, keyPattern);
  }
  return data;
}
function redactBreadcrumbData(data) {
  if (!data) return data;
  let result = data;
  if (typeof data === "object") {
    result = _objectSpread({}, data);
  } else if (Array.isArray(data)) {
    // Workaround TypeScript limitation to map types between `data` and `result`
    result = [...data];
  } else {
    return data;
  }
  for (const k in data) {
    if (Object.prototype.hasOwnProperty.call(data, k)) {
      const v = data[k];
      if (typeof v === "string" && isHex(v)) {
        // Workaround TypeScript limitation to map types between `data` and `result`
        result[k] = "***";
      } else {
        result[k] = redactBreadcrumbData(v);
      }
    }
  }
  return result;
}

/**
 * @deprecated Use `redactEventData` instead
 */
function redactData(data) {
  let keyPattern = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultPattern;
  return redactEventData(data, keyPattern);
}

export { createLogger, LoglevelSentry as default, redactBreadcrumbData, redactData, redactEventData };
//# sourceMappingURL=loglevelSentry.esm.js.map
